import { concat, get, gt, isEmpty, map, merge, size } from 'lodash';
import { Injectable } from '@angular/core';

import { lazyInject } from '@app/containers/base/decorators/lazy-inject.decorator';
import { Observable, observableForkJoin, rx } from '@app/shared/rx/rx-wrapper';
import {
  IBaseEntityResponse,
  IEvent,
  IFilterQuery,
  IInventoryItem,
} from '@app/shared/types';
import { MAX_PAGE_SIZE } from '@config/global-constants';

import { DmDataService } from '../../dm-data/dm-data.service';
import { InventoryItemVenueService } from '../../inventory-item-venue/inventory-item-venue.service';
import { IReplenishPercentMap } from '../replenish.types';
import { AbstractReplenishDataService } from './abstract-replenish-data.service';
import { ReplenishDataSource } from './replenish-data.enums';
import { buildUniqSites } from '../replenish.utils';
import { ReplenishItemSiteType } from '../replenish.enums';

@Injectable()
class ReplenishInventoryDataService extends AbstractReplenishDataService<
  IBaseEntityResponse<IInventoryItem>
> {
  public static SOURCE_NAME: string = ReplenishDataSource.InventoryItemVenue;

  @lazyInject(InventoryItemVenueService)
  public inventoryItemVenueService: InventoryItemVenueService;
  @lazyInject(DmDataService) public dmDataService: DmDataService;

  public buildQueryFilter(event?: IEvent): string {
    return isEmpty(event)
      ? 'is_master eq false and thresholds/type eq in(default)'
      : `is_master eq false and thresholds/type eq in(event_type) and thresholds/event_type_entity_id eq in(${event.event_type_entity_id})`;
  }

  public buildSelectFields(): string[] {
    return [
      'code',
      'reporting_category_major',
      'reporting_category_minor',
      'name',
      'gl_account',
      'measures',
      'vendors',
      'preferred_vendor',
      'thresholds',
      'costs',
      'site_id',
      'is_chargeable',
    ];
  }

  public getItemDataObs(
    siteIds: string[],
    useSimpleFilter: boolean,
    fetchItemParams: {
      filter: string;
      select: string[];
      top: number;
    } & IFilterQuery,
  ): Observable<{ items: IBaseEntityResponse[]; total: number }> {
    return gt(size(siteIds), 1)
      ? this.dmDataService.getSiteGroupIdList(siteIds).pipe(
          rx.mergeMap((groupIds: string[]) =>
            this.inventoryItemVenueService.batchFetchCurrentList(groupIds, {
              ...fetchItemParams,
              masterFilter: 'measures/transactions/transfer eq in(true)',
              filter: useSimpleFilter
                ? 'measures/transactions/transfer eq in(true) and is_master eq false'
                : fetchItemParams.filter,
            }),
          ),
        )
      : this.inventoryItemVenueService
          .fetchCurrentList(
            {
              ...fetchItemParams,
              filter: useSimpleFilter
                ? 'measures/transactions/transfer eq in(true) and is_master eq false'
                : fetchItemParams.filter,
            },
            siteIds,
          )
          .pipe(
            rx.map(
              (currentResult) => {
                const items = get(currentResult, 'items', []);

                return { items, total: items.length };
              },
              <IBaseEntityResponse>{ items: [], total: 0 },
            ),
          );
  }

  public fetch(params: {
    percentDesMap: IReplenishPercentMap;
    filterQuery?: IFilterQuery;
    event?: IEvent;
    useSimpleFilter?: boolean;
  }): Observable<IBaseEntityResponse<IInventoryItem>> {
    const { filterQuery } = params;

    const queryFilter = this.buildQueryFilter(params.event);
    const selectFields = this.buildSelectFields();

    const fetchInventoryItemParams = merge(
      {
        filter: queryFilter,
        select: selectFields,
        top: MAX_PAGE_SIZE,
      },
      filterQuery,
    );

    const fromSites = buildUniqSites(params.percentDesMap, 'fromSite');
    const toSites = buildUniqSites(params.percentDesMap, 'toSite');

    return observableForkJoin([
      this.getItemDataObs(
        fromSites,
        get(params, 'useSimpleFilter', true),
        fetchInventoryItemParams,
      ),
      this.getItemDataObs(
        toSites,
        get(params, 'useSimpleFilter', false),
        fetchInventoryItemParams,
      ),
    ]).pipe(
      rx.map(([fromInventoryItems, toInventoryItems]) => {
        const items = concat(
          map(get(fromInventoryItems, 'items', []), (item) => {
            return {
              ...item,
              origin_measures: item.measures || [],
              siteType: ReplenishItemSiteType.From,
            };
          }),
          map(get(toInventoryItems, 'items', []), (item) => {
            return {
              ...item,
              origin_measures: item.measures || [],
              siteType: ReplenishItemSiteType.To,
            };
          }),
        );

        return {
          items,
          total: items.length,
        } as IBaseEntityResponse<IInventoryItem>;
      }),
    );
  }
}

export { ReplenishInventoryDataService };
